import classnames from 'classnames';
import * as React from 'react';

import { getTransitionDurationFromElement } from '../../helpers/dom';
import DrawerBody from './DrawerBody';
import DrawerFooter from './DrawerFooter';
import DrawerHeader from './DrawerHeader';
import { DrawerProps } from './types';

let stealFocusTimeout: number;
let returnFocusTimeout: number;

export const Drawer: React.FC<DrawerProps> = ({
	isOpen,
	options = {},
	onClose,
	children,
}) => {
	const closeCtaRef = React.useRef<HTMLButtonElement>(null);
	const [transitionDuration, setTransitionDuration] = React.useState(-1);
	const { elementToReturnFocusOnClose, ...otherProps } = options;

	React.useEffect(() => {
		if (isOpen) {
			clearTimeout(returnFocusTimeout);
			stealFocusTimeout = window.setTimeout(() => {
				if (closeCtaRef.current) {
					closeCtaRef.current.focus();
				}
			}, 500); // The delay is required to prevent a janky transition. The delay is the same length as the drawer slide CSS transition
		}
		if (!isOpen) {
			clearTimeout(stealFocusTimeout);
			returnFocusTimeout = window.setTimeout(() => {
				if (elementToReturnFocusOnClose) {
					elementToReturnFocusOnClose.focus();
				}
			}, 0);
		}

		return () => {
			clearTimeout(stealFocusTimeout);
			clearTimeout(returnFocusTimeout);
		};
	}, [elementToReturnFocusOnClose, isOpen]);

	return (
		<div
			ref={(node) => {
				if (node) {
					if (isOpen) {
						node.removeAttribute('inert');
					} else {
						node.setAttribute('inert', '');
					}
					if (transitionDuration === -1) {
						const duration = getTransitionDurationFromElement(node);

						setTransitionDuration(duration ? duration : 0);
					}
				}
			}}
			data-testid="chatbot-drawer"
			hidden={!isOpen}
			role="dialog"
			className={classnames(
				'c-chatbot-drawer overflow-hidden absolute top-0 bottom-0 flex flex-col w-full bg-white z-50',
				{
					'c-chatbot-drawer--open': isOpen,
				}
			)}
			{...otherProps}
		>
			<DrawerHeader onClose={onClose} ref={closeCtaRef}></DrawerHeader>
			<DrawerBody>{children}</DrawerBody>
			<DrawerFooter></DrawerFooter>
		</div>
	);
};

export default Drawer;
